/**
 * Public holidays, by country and year.
 *
 * Moved out of `calendar.ts`, where `holidays(y)` returned exactly two sets,
 * `de` and `ie`: Berlin's and the one person's second home. A second home is
 * the profile's now (`profiles.second_home.country`), so the sets are keyed by
 * what the profile stores. `'DE-BE'` is Berlin's own (it has International
 * Women's Day and Germany does not), `'IE'` is Ireland's, `'US-MA'` is Massachusetts'; any other code has no
 * table and answers `[]` — no holidays drawn, which is the honest answer for a
 * country nobody has written down, and not Ireland's by default.
 *
 * Computed, not stored: a holiday is a function of the year and the country,
 * and a table would be a cache that goes stale. No imports and its own date
 * helpers, because `calendar.ts` imports *this* and the root suite loads both
 * with `node --test`, whose resolver will not guess an extension.
 */

export interface Holiday { on: string; name: string }

const DAY = 86_400_000
const D = (s: string) => new Date(s + 'T12:00:00Z')
const addDays = (s: string, n: number) => new Date(D(s).getTime() + n * DAY).toISOString().slice(0, 10)
const pad = (n: number) => String(n).padStart(2, '0')

/** Meeus/Jones/Butcher. Good for any Gregorian year. */
export function easter(y: number): string {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const mo = Math.floor((h + l - 7 * m + 114) / 31)
  const da = ((h + l - 7 * m + 114) % 31) + 1
  return `${y}-${pad(mo)}-${pad(da)}`
}

const nthWeekday = (y: number, month: number, weekday: number, n: number) => {
  let d = `${y}-${pad(month)}-01`
  while (D(d).getUTCDay() !== weekday) d = addDays(d, 1)
  return addDays(d, (n - 1) * 7)
}
const lastWeekday = (y: number, month: number, weekday: number) => {
  let d = `${y}-${pad(month)}-${new Date(Date.UTC(y, month, 0)).getUTCDate()}`
  while (D(d).getUTCDay() !== weekday) d = addDays(d, -1)
  return d
}

function berlin(y: number): Holiday[] {
  const E = easter(y)
  return [
    { on: `${y}-01-01`, name: 'New Year' },
    { on: `${y}-03-08`, name: "International Women's Day" },   // Berlin only
    { on: addDays(E, -2), name: 'Good Friday' },
    { on: addDays(E, 1), name: 'Easter Monday' },
    { on: `${y}-05-01`, name: 'Labour Day' },
    { on: addDays(E, 39), name: 'Ascension' },
    { on: addDays(E, 50), name: 'Whit Monday' },
    { on: `${y}-10-03`, name: 'German Unity Day' },
    { on: `${y}-12-25`, name: 'Christmas Day' },
    { on: `${y}-12-26`, name: 'Boxing Day' },
  ]
}

function ireland(y: number): Holiday[] {
  const E = easter(y)
  // Ireland moves a holiday that lands on a weekend to the next free weekday;
  // Germany does not, which is why only this table substitutes. It bites in
  // 2027, where Christmas is a Saturday and St Stephen's a Sunday, so the days
  // actually off are Monday the 27th and Tuesday the 28th. Applied in order so
  // two consecutive weekend holidays do not both land on the same Monday.
  const taken = new Set<string>()
  const observed = (d: string): string => {
    let out = d
    while (D(out).getUTCDay() === 0 || D(out).getUTCDay() === 6 || taken.has(out)) out = addDays(out, 1)
    taken.add(out)
    return out
  }
  return [
    { on: observed(`${y}-01-01`), name: 'New Year' },
    { on: observed(nthWeekday(y, 2, 1, 1)), name: "St Brigid's Day" },
    { on: observed(`${y}-03-17`), name: "St Patrick's Day" },
    { on: observed(addDays(E, 1)), name: 'Easter Monday' },
    { on: observed(nthWeekday(y, 5, 1, 1)), name: 'May Bank Holiday' },
    { on: observed(nthWeekday(y, 6, 1, 1)), name: 'June Bank Holiday' },
    { on: observed(nthWeekday(y, 8, 1, 1)), name: 'August Bank Holiday' },
    { on: observed(lastWeekday(y, 10, 1)), name: 'October Bank Holiday' },
    { on: observed(`${y}-12-25`), name: 'Christmas Day' },
    { on: observed(`${y}-12-26`), name: "St Stephen's Day" },
  ]
}

/**
 * Massachusetts: the federal holidays plus Patriots' Day (cities plan C2.3,
 * D4). Evacuation Day (17 Mar) and Bunker Hill Day (17 Jun) are Suffolk
 * County's only, which is Boston, Chelsea, Revere and Winthrop and not
 * Concord, and are left out rather than drawn for the wrong half of the map.
 *
 * Observed days follow the federal rule: a Saturday holiday is taken on the
 * Friday, a Sunday one on the Monday. Only the observed day is listed — the
 * Saturday itself is a weekend either way, and a day off is what a calendar
 * of holidays is asked about (Ireland's table does the same). A table is a
 * function of one year, so New Year's Day of next year, when it falls on a
 * Saturday and is taken on 31 December, is listed in *this* year's table and
 * left out of next year's: `holidaysIn` asks each year its own table, and a
 * range ending on 31 December would otherwise never see it.
 */
function massachusetts(y: number): Holiday[] {
  const weekday = (d: string) => D(d).getUTCDay()
  const observed = (d: string, name: string): Holiday => {
    const wd = weekday(d)
    const on = wd === 6 ? addDays(d, -1) : wd === 0 ? addDays(d, 1) : d
    return { on, name: on === d ? name : `${name} (observed)` }
  }
  const out: Holiday[] = [
    { on: nthWeekday(y, 1, 1, 3), name: 'Martin Luther King Jr. Day' },
    { on: nthWeekday(y, 2, 1, 3), name: "Presidents' Day" },
    { on: nthWeekday(y, 4, 1, 3), name: "Patriots' Day" },
    { on: lastWeekday(y, 5, 1), name: 'Memorial Day' },
    observed(`${y}-06-19`, 'Juneteenth'),
    observed(`${y}-07-04`, 'Independence Day'),
    { on: nthWeekday(y, 9, 1, 1), name: 'Labor Day' },
    { on: nthWeekday(y, 10, 1, 2), name: "Indigenous Peoples' Day" },
    observed(`${y}-11-11`, 'Veterans Day'),
    { on: nthWeekday(y, 11, 4, 4), name: 'Thanksgiving' },
    observed(`${y}-12-25`, 'Christmas Day'),
  ]
  if (weekday(`${y}-01-01`) !== 6) out.push(observed(`${y}-01-01`, "New Year's Day"))
  if (weekday(`${y + 1}-01-01`) === 6) out.push({ on: `${y}-12-31`, name: "New Year's Day (observed)" })
  return out
}

// A `Map`, not an object literal: the country is a profile's free text, and
// `TABLES['constructor']` would answer out of `Object.prototype` (CLAUDE.md's
// alias-map bug) — a lookup that decides both whether a country is known and
// what it maps to.
const TABLES = new Map<string, (y: number) => Holiday[]>([
  ['DE-BE', berlin],
  ['IE', ireland],
  ['US-MA', massachusetts],
])

const cache = new Map<string, Holiday[]>()

/** The public holidays of `country` in `year`, by date; `[]` for a country with
 *  no table. The code is compared as written, upper-cased. */
export function holidaysFor(country: string | null | undefined, year: number): Holiday[] {
  const key = (country ?? '').trim().toUpperCase()
  const table = TABLES.get(key)
  if (!table) return []
  const hit = cache.get(`${key}|${year}`)
  if (hit) return hit
  const out = table(year).sort((a, b) => (a.on < b.on ? -1 : a.on > b.on ? 1 : 0))
  cache.set(`${key}|${year}`, out)
  return out
}

/** The same holidays keyed by day, for the callers that ask "is this day one". */
export function holidayMap(country: string | null | undefined, year: number): Record<string, string> {
  return Object.fromEntries(holidaysFor(country, year).map(h => [h.on, h.name]))
}

/** Whether `holidaysFor` has anything to say about this country. */
export const hasHolidays = (country: string | null | undefined): boolean =>
  TABLES.has((country ?? '').trim().toUpperCase())
